<template>
  <div :class="$setClasses" ref="installmentConfirmRef">
    <p v-if="subtitle" :class="$vClass('subtitle')" v-html="subtitle"></p>
    <div :class="$vClass('wrapper')" v-loading="installmentSimulatedPlanLoading">
      <div :class="$vClass('before')">
        <installment-plan :offer="offer"
          :options-content="optionsContent"
          :plan-content="optionsContent?.plan || {}"
        />
        <card-flex
          v-if="notice && !isMobile"
          :class="[
            $vClass('memo'),
            'u-border-none',
            'step-comparator-wrapper__header__card-flex u-margin-top-default',
          ]"
          :content="notice"
          no-box-shadow
        />
      </div>
      <div :class="$vClass('after')">
        <div :class="$vClass('after__content')">
          <p
            v-if="installmentSimulatedPlan"
            :class="$vClass('after__total-amount')"
            v-html="
              getTextVersion(installmentContent.after?.totalAmount || '', {
                amount: formatAmount(installmentSimulatedPlan?.customerTotalAmount),
                fees: formatAmount(installmentSimulatedPlan?.customerFees),
              })
            "
          ></p>
        </div>
        <div v-if="!isMobile" :class="$vClass('after__image')">
          <img :src="$t('payment.installment.image')" width="200" alt="Installment Payment" />
        </div>
        <card-flex
          v-else-if="notice"
          :class="[$vClass('memo'), 'u-border-none']"
          :content="notice"
          no-box-shadow
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  useMoveManager,
  useMoveManagerEmits,
  useMoveManagerProps,
} from "@/composables/useMoveManager";
import formatHelpers from "@/utils/helpers/format";
import { storeToRefs } from "pinia";
import { useInstallmentStore, useDimensionStore } from "@/stores";
import InstallmentPlan from "./InstallmentPlan.vue";
import { computed, useTemplateRef } from "vue";
import CardFlex from "@/utils/components/CardFlex.vue";

defineOptions({
  name: "InstallmentConfirm",
  nameClassStyle: "installment-confirm",
});

const props = defineProps({
  ...useMoveManagerProps,
  optionsContent: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(useMoveManagerEmits);

const installmentStore = useInstallmentStore();
const { installmentSimulatedPlans, installmentSimulatedPlanLoading } = storeToRefs(installmentStore);

const dimensionStore = useDimensionStore();
const { isMobile } = storeToRefs(dimensionStore);

const context = useMoveManager({ offer: props.offer }, { emit });
const { getTextVersion, getTextParams } = context;

const installmentSimulatedPlan = computed(() => installmentSimulatedPlans.value[0]);

const installmentContent = computed(() => {
  return props.optionsContent;
});

const confirmContent = computed(() => {
  return props.optionsContent?.confirm;
});

const subtitle = computed(() => {
  return getTextVersion(confirmContent.value?.subtitle, getTextParams.value);
});

const notice = computed(() => {
  return getTextVersion(confirmContent.value?.notice, getTextParams.value);
});

const formatAmount = (amount: number) => formatHelpers.formatAmount(amount);

const installmentConfirmRef = useTemplateRef("installmentConfirmRef");
defineExpose({
  installmentConfirmRef: installmentConfirmRef,
});
</script>

<style scoped lang="scss">
.installment-confirm {
  --border: 2px solid var(--nxt-color-grey-border);

  &__wrapper {
    @include breakpoint(tablet) {
      display: grid;
      grid-template-columns: 2fr 1fr;
    }
  }

  &__subtitle {
    margin-bottom: var(--nxt-space-default);
  }

  &__before {
    flex: 1;
    height: 100%;
    border: var(--border);
    padding: var(--nxt-space-default);

    :deep(.installment-plan) {
      height: auto;

      .installment-plan__subtitle {
        margin-top: 0;
        margin-bottom: var(--nxt-space-default);
        line-height: 1;
        font-size: var(--nxt-font-size-large);
        font-weight: var(--nxt-font-weight-bold);
      }
    }

    :deep(.installment-plan__item__before) {
      margin-right: 0;
      font-weight: var(--nxt-font-weight-bold);

      @include breakpoint(tablet) {
        margin-right: var(--nxt-space-small);
      }
    }

    :deep(.installment-plan__item__after) {
      font-weight: var(--nxt-font-weight-medium);
    }
  }

  &__after {
    margin-top: var(--nxt-space-default);

    @include breakpoint(tablet) {
      display: flex;
      flex-direction: column;
      margin: 0;
      padding: var(--nxt-space-default);
      border-top: var(--border);
      border-bottom: var(--border);
      border-right: var(--border);
    }

    &__content {
      text-align: center;
      border: 1px solid var(--nxt-color-success);
      border-radius: var(--nxt-border-radius-base);
      padding: var(--nxt-space-default);
      background-color: #f7f7f7;

      :deep(.price-title) {
        font-size: var(--nxt-font-size-xlarge);
      }

      :deep(.price-title),
      :deep(.price-amount) {
        display: block;
      }

      :deep(.price-amount) {
        padding-top: var(--nxt-space-small);
        font-size: 30px;
      }

      :deep(.price-fees) {
        display: block;
        padding-bottom: var(--nxt-space-small);
        font-size: var(--nxt-font-size-small);
      }

      @include breakpoint(tablet) {
        padding: var(--nxt-space-default);
      }
    }

    &__image {
      @include breakpoint(tablet) {
        flex: 1;
        display: flex;
        justify-content: center;
      }
    }
  }

  :deep(.installment-confirm__memo),
  &__memo {
    margin: 0;
    border-radius: 20px;

    .el-card__body {
      padding: var(--el-card-padding) 0 0;
    }

    @include breakpoint(tablet) {
      margin-top: var(--nxt-space-default);

      .el-card__body {
        padding: var(--el-card-padding);
      }
    }

    .notice-title {
      font-size: 22px;
      font-weight: var(--nxt-font-weight-normal);
    }
  }
}
</style>
